<template>
  <div>
    <NavBar />
    <div class="container">
      <div class="row">
        <div class="col">
          <h1>
            Users <small>{{ organizationDisplayName }}</small>
          </h1>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="d-flex py-4">
            <form class="form-inline w-75" @submit.prevent>
              <div class="ml-auto w-100">
                <div class="form-group d-flex">
                  <input type="text" class="form-control mr-2 flex-fill" placeholder="Search a user by Full Name or Email Address" v-model="query" />
                  <button type="button" class="btn btn-primary mr-2" @click="search">
                    <fa icon="search" />
                    <span class="ml-2">Search</span>
                  </button>
                  <button type="button" class="btn btn-primary" @click="refresh">
                    <fa icon="redo" />
                    <span class="ml-2">Refresh</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div class="col-md-auto">
          <div class="d-flex py-4">
            <div class="ml-auto">
              <button type="button" class="btn btn-primary" @click="create">
                <fa icon="plus" />
                <span class="ml-2">Add User</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="table-responsive">
            <table class="table table-hover">
              <thead class="thead-light">
                <tr>
                  <th scope="col">Full Name</th>
                  <th scope="col">Email Address</th>
                  <th scope="col" class="text-center">Verified</th>
                  <th scope="col" class="text-center">Enabled</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="isLoading" class="text-center text-muted">
                  <td colspan="4"><fa icon="circle-notch" spin /> Loading...</td>
                </tr>
                <tr v-if="error" class="text-center text-muted">
                  <td colspan="4">Sorry. There was an error. Please try again.</td>
                </tr>
                <tr v-if="page.total === 0" class="text-center text-muted">
                  <td colspan="4">No results</td>
                </tr>
                <tr v-for="u in page.items" :key="u.id">
                  <td @click="edit(u)">{{ u.fullName }}</td>
                  <td @click="edit(u)">{{ u.emailAddress }}</td>
                  <td @click="edit(u)" class="text-center">
                    <fa v-if="isVerified(u)" icon="check" class="text-success" />
                  </td>
                  <td class="text-center">
                    <div v-if="isNotSelf(u)" class="custom-control custom-switch">
                      <input type="checkbox" class="custom-control-input" :id="u.id" :checked="isEnabled(u)" @change="toggle(u)" />
                      <label class="custom-control-label" :for="u.id"></label>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div v-if="needsPaging" class="row">
        <div class="col">
          <div class="row justify-content-between p-3">
            <button type="button" class="btn btn-outline-dark" @click="prev" :disabled="!hasPrev">Previous</button>
            <button type="button" class="btn btn-outline-dark" @click="next" :disabled="!hasNext">Next</button>
          </div>
        </div>
      </div>
    </div>
    <!-- Modal -->
    <div ref="modal" class="modal fade" tabindex="-1" role="dialog" aria-labelledby="modal" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header border-0">
            <h5 class="modal-title text-capitalize">{{ getUserAction(selectedUser) }} User</h5>
            <button type="button" class="close" aria-label="Close" data-dismiss="modal" @click="closeModal()">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <p class="lead">Are you sure you want to {{ getUserAction(selectedUser) }} this user?</p>
            <dl class="row mb-0">
              <dt class="col-sm-3">Full Name</dt>
              <dd class="col-sm-9" v-if="selectedUser">{{ selectedUser.fullName }}</dd>
              <dt class="col-sm-3">Email Address</dt>
              <dd class="col-sm-9" v-if="selectedUser">{{ selectedUser.emailAddress }}</dd>
            </dl>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-primary text-capitalize" @click="submit()">{{ getUserAction(selectedUser) }}</button>
            <button type="button" class="btn btn-outline-primary" @click="closeModal()" data-dismiss="modal">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import { mapGetters } from "vuex";
import debounce from "debounce";
import NavBar from "@/views/reports/NavBar.vue";

export default {
  components: {
    NavBar,
  },
  props: {
    organizationId: String,
  },
  data() {
    return {
      isLoading: false,

      skip: 0,
      take: 25,
      query: null,

      page: {},
      error: null,

      selectedUser: null,
    };
  },
  computed: {
    ...mapGetters("organization", ["organization"]),
    ...mapGetters("user", ["user"]),
    ...mapGetters("role", ["isVerensicsAdministrator"]),
    hasPrev() {
      if (!this.page) return false;
      return this.skip > 0;
    },
    hasNext() {
      if (!this.page) return false;
      return this.skip + this.take < this.page.total;
    },
    needsPaging() {
      return this.hasNext || this.hasPrev;
    },
    url() {
      let url = `/v1/organizations/${this.organizationId}/users?skip=${this.skip}&take=${this.take}`;
      url += this.query ? `&query=${encodeURIComponent(this.query)}` : "";
      return url;
    },
    organizationDisplayName() {
      if (!this.isVerensicsAdministrator) return null;
      if (this.organizationId === this.organization.id) return `(${this.organization.displayName})`;
      return this.page && this.page.items && this.page.items.length ? `(${this.page.items[0].organization})` : null;
    },
  },
  watch: {
    skip() {
      this.load();
    },
    take() {
      this.load();
    },
    query() {
      this.skip = 0;
      this.load();
    },
    user() {
      this.skip = 0;
      this.load();
    },
    organization() {
      this.skip = 0;
      this.load();
    },
    organizationId() {
      this.skip = 0;
      this.load();
    },
  },
  created() {
    this.load = debounce(this.load, 350);
    this.load();
  },
  methods: {
    load() {
      this.page = {};
      this.error = null;
      this.isLoading = true;
      this.$axios
        .get(this.url)
        .then(res => (this.page = res.data))
        .catch(err => (this.error = err))
        .finally(() => (this.isLoading = false));
    },
    prev() {
      this.skip = Math.max(0, this.skip - this.take);
    },
    next() {
      this.skip += this.take;
    },
    create() {
      this.$router.push(`/organizations/${this.organizationId}/users/create`);
    },
    edit(user) {
      this.$router.push(`/organizations/${this.organizationId}/users/${user.id}`);
    },
    showModal(user) {
      this.selectedUser = user;
      $(this.$refs.modal).modal("show");
    },
    closeModal() {
      this.selectedUser = null;
      $(this.$refs.modal).modal("hide");
      this.load();
    },
    toggle(user) {
      this.showModal(user);
    },
    submit() {
      if (this.isEnabled(this.selectedUser)) {
        this.disable();
      } else {
        this.enable();
      }
    },
    disable() {
      this.$axios.post(`/v1/organizations/${this.organizationId}/users/${this.selectedUser.id}/disable`).finally(() => {
        this.closeModal();
      });
    },
    enable() {
      this.$axios.post(`/v1/organizations/${this.organizationId}/users/${this.selectedUser.id}/enable`).finally(() => {
        this.closeModal();
      });
    },
    search() {
      this.load();
    },
    refresh() {
      this.load();
    },
    getUserAction(user) {
      if (!user) return "";
      return this.isEnabled(user) ? "disable" : "enable";
    },
    isVerified(user) {
      return user && (!!user.emailAddressVerified || !!user.mobilePhoneNumberVerified);
    },
    isEnabled(user) {
      return user.status === "enabled";
    },
    isNotSelf(user) {
      return this.user && this.user.id !== user.id;
    },
  },
};
</script>

<style lang="scss" scoped>
tbody tr {
  cursor: pointer;
}

.custom-control-label {
  cursor: pointer;
}
</style>
